/**
 * @module UseReasoning
 * @purpose Hooks for the reasoning-engine panel: polling, clusters, observation resolution, detail.
 *   Force-recompute moved to use-force-recompute.ts.
 * @depends services/reasoning, types/reasoning
 * @dependedOnBy pages/case/analysis/reasoning/CanvasReasoningPanel, ReasoningCtaButton
 *
 * @rules
 * - Performance: stale-request guard on useReasoningRun; setTimeout-recursion prevents overlapping polls
 * - State: local useState per hook; no shared context
 * - Errors: error shape is string | null; ApiError.message extracted in catch blocks
 *
 * @contract useReasoningRun's `run` answers WHAT IS THE ENGINE DOING -- it drives the lock,
 *   the dialog variant, and the running/notStarted/failed body-state selection. `completedRun`
 *   answers WHAT RESULTS EXIST -- it drives every surface that renders analysis output. A
 *   consumer reads exactly one of the two for any given surface.
 *   (HypothesesView/BriefingBoardView/ReasoningCtaButton keep reading `run` in this
 *   PRP -- Non-Goal 5.)
 */

import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'

import {
  getCluster,
  getLatestReasoningRun,
  listClusters,
  resolveObservations,
} from '../services/reasoning'
import type {
  ClusterDetail,
  ClusterSummary,
  ReasoningRun,
  ReasoningRunStatus,
  ResolvedObservation,
} from '../types/reasoning'

const TERMINAL_STATUSES: ReadonlySet<ReasoningRunStatus> = new Set([
  'completed',
  'failed',
  'superseded',
])

const POLL_INTERVAL_MS = 2000
const DEFAULT_CLUSTER_PAGE_SIZE = 20
const MAX_RESOLVE_BATCH = 200

// =============================================================================
// useReasoningRun — module-level helpers
// =============================================================================

interface ReasoningRunState {
  requestId: number
  timeout: ReturnType<typeof setTimeout> | null
  mounted: boolean
  enabled: boolean
  caseId: string | null
}

type RunSetter = (run: ReasoningRun | null) => void
type ErrorSetter = (err: string | null) => void

interface PollCtx {
  stateRef: MutableRefObject<ReasoningRunState>
  thisId: number
  setRun: RunSetter
  setError: ErrorSetter
}

function isTerminalRun(run: ReasoningRun): boolean {
  return TERMINAL_STATUSES.has(run.status)
}

function deriveCompletedRun(run: ReasoningRun | null): ReasoningRun | null {
  if (run === null) return null
  if (run.status === 'completed') return run
  return run.latestCompleted ?? null
}

function shouldStopPoll(
  st: ReasoningRunState,
  thisId: number,
  run: ReasoningRun | null,
): boolean {
  // A null run means the case has never been reasoned over -- nothing to poll.
  if (run === null) return true
  return !st.mounted || !st.enabled || isTerminalRun(run) || thisId !== st.requestId
}

function schedulePoll(ctx: PollCtx, previousRun: ReasoningRun | null): void {
  const { stateRef, thisId, setRun, setError } = ctx
  const st = stateRef.current
  if (shouldStopPoll(st, thisId, previousRun)) return

  st.timeout = setTimeout(() => {
    const inner = stateRef.current
    if (shouldStopPoll(inner, thisId, previousRun)) return
    void getLatestReasoningRun(inner.caseId ?? '')
      .then((latest) => {
        if (!inner.mounted || thisId !== inner.requestId) return
        setRun(latest)
        setError(null)
        schedulePoll(ctx, latest)
      })
      .catch(() => {
        if (!inner.mounted || thisId !== inner.requestId) return
        schedulePoll(ctx, previousRun)
      })
  }, POLL_INTERVAL_MS)
}

async function performInitialFetch(
  ctx: PollCtx,
  setLoading: (v: boolean) => void,
): Promise<void> {
  const { stateRef, thisId, setRun, setError } = ctx
  const st = stateRef.current

  try {
    const latest = await getLatestReasoningRun(st.caseId ?? '')
    if (!st.mounted || thisId !== st.requestId) return
    setRun(latest)
    setError(null)
    schedulePoll(ctx, latest)
  } catch (err) {
    if (!st.mounted || thisId !== st.requestId) return
    setError(err instanceof Error ? err.message : 'Failed to fetch reasoning run')
  } finally {
    if (st.mounted && thisId === st.requestId) setLoading(false)
  }
}

// =============================================================================
// useReasoningRun
// =============================================================================

/**
 * Polls /reasoning-runs/latest every 2 s while status ∈ {pending, running}.
 * Stops on terminal status, enabled=false, or component unmount.
 *
 * @behaviour setTimeout-recursion avoids overlapping requests when a single poll exceeds 2 s.
 *            Stale-request guard prevents out-of-order state writes.
 *            completedRun is derived from run with no extra state or fetch: null when run is
 *            null, run itself when run.status is 'completed', otherwise run.latestCompleted
 *            (or null). See the module doc header for the run-vs-completedRun contract.
 * @errors Sets error string; polling continues on transient errors.
 */
export function useReasoningRun(
  caseId: string | null,
  enabled: boolean,
): {
  run: ReasoningRun | null
  completedRun: ReasoningRun | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
} {
  const [run, setRun] = useState<ReasoningRun | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const stateRef = useRef<ReasoningRunState>({
    requestId: 0,
    timeout: null,
    mounted: false,
    enabled,
    caseId,
  })
  stateRef.current.enabled = enabled
  stateRef.current.caseId = caseId

  const fetch = useCallback(async () => {
    const st = stateRef.current
    if (!st.caseId || !st.enabled) {
      setRun(null)
      setError(null)
      setLoading(false)
      return
    }

    st.requestId += 1
    const thisId = st.requestId
    if (st.timeout !== null) { clearTimeout(st.timeout); st.timeout = null }

    setLoading(true)
    setError(null)

    await performInitialFetch({ stateRef, thisId, setRun, setError }, setLoading)
  }, [])

  useEffect(() => {
    const st = stateRef.current
    st.mounted = true
    void fetch()

    return () => {
      st.mounted = false
      if (st.timeout !== null) { clearTimeout(st.timeout); st.timeout = null }
    }
  }, [fetch])

  useEffect(() => {
    if (!enabled) {
      const st = stateRef.current
      st.requestId += 1
      if (st.timeout !== null) { clearTimeout(st.timeout); st.timeout = null }
      setRun(null)
      setError(null)
      setLoading(false)
    }
  }, [enabled])

  return { run, completedRun: deriveCompletedRun(run), loading, error, refetch: fetch }
}

// =============================================================================
// useReasoningClusters
// =============================================================================

/**
 * Offset-paginated list of clusters for a given run.
 *
 * @behaviour Resets and re-fetches on caseId or runId change.
 *            loadMore appends next page; deduplicates by clusterId.
 * @errors Sets error string on failure; clears on next successful fetch.
 */
export function useReasoningClusters(
  caseId: string | null,
  runId: string | null,
  pageSize: number = DEFAULT_CLUSTER_PAGE_SIZE,
): {
  clusters: ClusterSummary[]
  total: number
  loading: boolean
  loadingMore: boolean
  error: string | null
  refetch: () => Promise<void>
  loadMore: () => Promise<void>
} {
  const [clusters, setClusters] = useState<ClusterSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const requestIdRef = useRef(0)

  const fetch = useCallback(async () => {
    if (!caseId || !runId) {
      setClusters([])
      setTotal(0)
      setError(null)
      setLoading(false)
      return
    }

    requestIdRef.current += 1
    const thisId = requestIdRef.current

    setLoading(true)
    setError(null)

    try {
      const page = await listClusters(caseId, runId, { limit: pageSize, offset: 0 })
      if (thisId !== requestIdRef.current) return
      setClusters(page.clusters)
      setTotal(page.total)
    } catch (err) {
      if (thisId !== requestIdRef.current) return
      setError(err instanceof Error ? err.message : 'Failed to fetch clusters')
      setClusters([])
      setTotal(0)
    } finally {
      if (thisId === requestIdRef.current) setLoading(false)
    }
  }, [caseId, runId, pageSize])

  const loadMore = useCallback(async () => {
    if (!caseId || !runId || loadingMore) return

    setLoadingMore(true)
    setError(null)

    try {
      const offset = clusters.length
      const page = await listClusters(caseId, runId, { limit: pageSize, offset })
      const existingIds = new Set(clusters.map((item) => item.clusterId))
      const newItems = page.clusters.filter((item) => !existingIds.has(item.clusterId))
      setClusters((prev) => [...prev, ...newItems])
      setTotal(page.total)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more clusters')
    } finally {
      setLoadingMore(false)
    }
  }, [caseId, runId, loadingMore, clusters, pageSize])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { clusters, total, loading, loadingMore, error, refetch: fetch, loadMore }
}

// =============================================================================
// useReasoningArtefactBatch helpers
// =============================================================================

function buildBatches(ids: string[]): string[][] {
  const batches: string[][] = []
  for (let offset = 0; offset < ids.length; offset += MAX_RESOLVE_BATCH) {
    batches.push(ids.slice(offset, offset + MAX_RESOLVE_BATCH))
  }
  return batches
}

async function fetchBatchedObservations(
  caseId: string,
  runId: string,
  batches: string[][],
): Promise<Map<string, ResolvedObservation>> {
  const results = await Promise.all(
    batches.map((batch) => resolveObservations(caseId, runId, batch)),
  )
  const merged = new Map<string, ResolvedObservation>()
  for (const { resolved } of results) {
    for (const obs of resolved) {
      merged.set(obs.observationId, obs)
    }
  }
  return merged
}

// =============================================================================
// useReasoningArtefactBatch
// =============================================================================

/**
 * Batches observation resolution into ≤200-ID calls; caches results by observationId.
 *
 * @behaviour Cache is tied to runId; resets when runId changes.
 *            Already-cached IDs are served from cache without a network call.
 * @errors Propagates as thrown Error from resolve; callers handle.
 */
export function useReasoningArtefactBatch(
  caseId: string | null,
  runId: string | null,
): {
  resolve: (observationIds: string[]) => Promise<Map<string, ResolvedObservation>>
  cache: Map<string, ResolvedObservation>
  resetCache: () => void
} {
  const [cache, setCache] = useState<Map<string, ResolvedObservation>>(new Map())

  useEffect(() => {
    setCache(new Map())
  }, [runId])

  const resetCache = useCallback(() => {
    setCache(new Map())
  }, [])

  const resolve = useCallback(
    async (observationIds: string[]): Promise<Map<string, ResolvedObservation>> => {
      if (!caseId || !runId) return new Map()

      const uncached = observationIds.filter((id) => !cache.has(id))
      let newEntries = new Map<string, ResolvedObservation>()

      if (uncached.length > 0) {
        const batches = buildBatches(uncached)
        newEntries = await fetchBatchedObservations(caseId, runId, batches)
        setCache((prev) => {
          const next = new Map(prev)
          for (const [key, val] of newEntries) next.set(key, val)
          return next
        })
      }

      const result = new Map<string, ResolvedObservation>()
      for (const id of observationIds) {
        const entry = cache.get(id) ?? newEntries.get(id)
        if (entry !== undefined) result.set(id, entry)
      }
      return result
    },
    [caseId, runId, cache],
  )

  return { resolve, cache, resetCache }
}

// =============================================================================
// useClusterDetail
// =============================================================================

/**
 * Fetches full cluster detail including enrichments.
 *
 * @behaviour Fetches when all three IDs are non-null; re-fetches when clusterId changes.
 *   refetch is the stable fetch callback — callers may await it after a mutation to surface
 *   persisted annotations (e.g. hash-conflict resolutions merged into file_hashes).
 * @errors Sets error string on failure.
 */
export function useClusterDetail(
  caseId: string | null,
  runId: string | null,
  clusterId: string | null,
): {
  detail: ClusterDetail | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
} {
  const [detail, setDetail] = useState<ClusterDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId || !runId || !clusterId) {
      setDetail(null)
      setError(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getCluster(caseId, runId, clusterId)
      setDetail(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch cluster detail')
      setDetail(null)
    } finally {
      setLoading(false)
    }
  }, [caseId, runId, clusterId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { detail, loading, error, refetch: fetch }
}
